/**
 * MONSTER TOWN — CONTENT REGISTRATION. SHARED FILE, ADD-A-LINE ONLY.
 *
 * This is the ONE file every crew edits, and the edit is always the same shape:
 * import your module's array and spread it into `ALL_CONTENT`. Keep your defs
 * in your own file so two crews never touch the same lines.
 *
 *   import { FURNITURE } from './furniture'      <- interiors crew
 *   import { TOWN_PROPS } from './townProps'     <- world crew
 *   import { VENUE_FIXTURES } from './venue'     <- venue crew
 *
 * `registerContent()` is called once at boot, before any crew installs.
 */

import { setCoinRules, setUnlockLadder, markFreeForever, type UnlockTier } from '../core/economy'
import { registerPlaceables } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { flowerPatch, pathStone, woodBlock } from './blocks'
import { BUILD_KIT } from './kit' // <- build crew: walls/roofs/floors/paths/fences
import { VENUE_CONTENT, VENUE_FREE_IDS } from './venue' // <- venue crew: shop, stations, economy props
import { RESTAURANT_CONTENT, RESTAURANT_FREE_IDS } from './restaurant' // <- venue crew: venue #2, a data drop
import { NPC_CONTENT, NPC_FREE_IDS } from './npcGifts' // <- npc crew: neighbour presents
import { FURNITURE } from './furniture' // <- interiors crew: house furniture
import { FREE_FURNITURE_IDS } from '../interior/ids'
import { TOWN_PROPS, TOWN_FREE_IDS } from './townProps' // <- world crew: trees, street furniture
import { registerErrands } from '../core/errands' // <- "A Day in Town": errands are data
import { ERRANDS } from './errands' // <- errand crew: every errand in the game
import { COOKING_CONTENT, COOKING_FREE_IDS, RECIPE_COLLECTIBLES } from './recipes' // <- cooking crew
import { ANIMAL_COLLECTIBLES, ZOO_CONTENT, ZOO_FREE_IDS } from './animals' // <- zoo crew: venue #3
import { registerCollectibles } from '../core/collection' // <- "A Day in Town": creatures are data
import { CREATURES } from './creatures' // <- fishing/bugs crew: every fish and bug

/** Every placeable in the game. Crews append their own arrays here. */
const ALL_CONTENT = [
  woodBlock,
  pathStone,
  flowerPatch,
  ...BUILD_KIT,
  ...VENUE_CONTENT,
  ...RESTAURANT_CONTENT,
  ...NPC_CONTENT,
  ...FURNITURE,
  ...TOWN_PROPS,
  ...COOKING_CONTENT,
  ...ZOO_CONTENT,
]

/**
 * Coin amounts. Balance is DATA — tune it here, never in engine code.
 * Coins come easily on purpose: this is never a grind (brief, Economy).
 */
const COIN_RULES = [
  { source: 'venue.served' as const, base: 6, bonus: 3 },
  { source: 'venue.tip' as const, base: 4, bonus: 4 },
  { source: 'venue.perfectOrder' as const, base: 8, bonus: 4 },
  { source: 'town.job' as const, base: 10, bonus: 5 },
  { source: 'town.visitor' as const, base: 5, bonus: 3 },
  { source: 'town.find' as const, base: 3, bonus: 2 },
  { source: 'daily.return' as const, base: 25, bonus: 15 },
  { source: 'daily.gift' as const, base: 20, bonus: 10 },
  { source: 'garden.harvest' as const, base: 12, bonus: 6 },
  { source: 'starter' as const, base: 60 },
  { source: 'debug' as const, base: 100 },
]

/**
 * The unlock ladder, driven by LIFETIME coins earned (never current balance, so
 * buying something can never un-unlock anything). Tiers are generous and close
 * together — the point is a steady drip of "ooh, new stuff", not a wall.
 * Crews append their tier grants here as content lands.
 */
const LADDER: UnlockTier[] = [
  { id: 'tier0', lifetimeEarned: 0, grants: [], icon: 'seedling' },
  { id: 'tier1', lifetimeEarned: 120, grants: [], icon: 'lamp' },
  { id: 'tier2', lifetimeEarned: 400, grants: [], icon: 'cupcake' },
  { id: 'tier3', lifetimeEarned: 1000, grants: [], icon: 'star' },
  { id: 'tier4', lifetimeEarned: 2500, grants: [], icon: 'crown' },
]

/** Plain furniture that must stay free forever even though it is not a block. */
const FREE_IDS: PlaceableId[] = [
  ...VENUE_FREE_IDS,
  ...RESTAURANT_FREE_IDS,
  ...NPC_FREE_IDS,
  ...FREE_FURNITURE_IDS,
  ...TOWN_FREE_IDS,
  ...COOKING_FREE_IDS,
  ...ZOO_FREE_IDS,
]

export function registerContent(): void {
  registerPlaceables(ALL_CONTENT)
  // Errands, creatures, recipes and routines are DATA too. A new one is an
  // entry in its own file plus one line here, and nothing else in the engine.
  registerErrands(ERRANDS)
  registerCollectibles(CREATURES)
  registerCollectibles(RECIPE_COLLECTIBLES)
  registerCollectibles(ANIMAL_COLLECTIBLES)
  setCoinRules(COIN_RULES)
  setUnlockLadder(LADDER)
  markFreeForever(...FREE_IDS)
}

export { ALL_CONTENT }
